import React from 'react'
import { PaginationProps } from './Pagination.types'

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
}) => {
  // Fixed sibling count to always show max 5 pages
  const siblingCount = 1

  const paginationRange = (() => {
    if (totalPages <= 5) {
      return range(1, totalPages)
    }

    const leftSiblingIndex = Math.max(currentPage - siblingCount, 1)
    const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages)

    const shouldShowLeftDots = leftSiblingIndex > 2
    const shouldShowRightDots = rightSiblingIndex < totalPages - 1

    const firstPageIndex = 1
    const lastPageIndex = totalPages

    if (!shouldShowLeftDots && shouldShowRightDots) {
      const leftItemCount = 3 + 2 * siblingCount
      const leftRange = range(1, leftItemCount)
      return [...leftRange, '...', totalPages]
    }

    if (shouldShowLeftDots && !shouldShowRightDots) {
      const rightItemCount = 3 + 2 * siblingCount
      return [
        firstPageIndex,
        '...',
        ...range(totalPages - rightItemCount + 1, totalPages),
      ]
    }

    if (shouldShowLeftDots && shouldShowRightDots) {
      return [
        firstPageIndex,
        '...',
        ...range(leftSiblingIndex, rightSiblingIndex),
        '...',
        lastPageIndex,
      ]
    }

    return range(1, totalPages)
  })()

  function range(start: number, end: number) {
    const length = end - start + 1
    return Array.from({ length }, (_, idx) => idx + start)
  }

  return (
    <div className="flex justify-center items-center gap-1 md:gap-2">
      {/* Previous Button */}
      <button
        onClick={() => currentPage !== 1 && onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`w-7 h-7 md:w-8 md:h-8 rounded-md flex items-center justify-center text-xs md:text-sm ${
          currentPage === 1
            ? 'bg-[var(--pagination-bg-disabled)] text-[var(--pagination-text-disabled)] cursor-not-allowed'
            : 'bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] border border-[var(--bet-type-border)] cursor-pointer hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)]'
        }`}
        aria-label="Previous"
      >
        {'<'}
      </button>

      {/* Page Numbers */}
      {paginationRange?.map((page, idx) => (
        <button
          key={idx}
          onClick={() => typeof page === 'number' && onPageChange(page)}
          disabled={page === '...'}
          className={`w-7 h-7 md:w-8 md:h-8 rounded-md text-xs md:text-sm flex items-center justify-center ${
            page === '...'
              ? 'bg-[var(--pagination-bg-disabled)] text-[var(--pagination-text-disabled)] cursor-not-allowed'
              : currentPage === page
                ? 'active-gradient text-[var(--pagination-text-active)]'
                : 'bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] border border-[var(--bet-type-border)] cursor-pointer hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)]'
          } `}
        >
          {page}
        </button>
      ))}

      {/* Next Button */}
      <button
        onClick={() =>
          currentPage !== totalPages && onPageChange(currentPage + 1)
        }
        disabled={currentPage === totalPages}
        className={`w-7 h-7 md:w-8 md:h-8 rounded-md flex items-center justify-center text-xs md:text-sm ${
          currentPage === totalPages
            ? 'bg-[var(--pagination-bg-disabled)] text-[var(--pagination-text-disabled)] cursor-not-allowed'
            : 'bg-[var(--bet-type-bg)] text-[var(--bet-type-text)] border border-[var(--bet-type-border)] cursor-pointer hover:bg-[var(--bet-type-hover-bg)] hover:text-[var(--bet-type-hover-text)] hover:border-[var(--bet-type-hover-border)]'
        } `}
        aria-label="Next"
      >
        {'>'}
      </button>
    </div>
  )
}

export default Pagination
